import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckIcon, ArrowUturnLeftIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import {
  getReviewItem, reviewCommitForm, reviewDiscard, reviewUndo, openReview, reviewAnalyze,
  getPatient, getPatientFile, type ReviewItemDetail, type Patient,
} from '../api/unimedica';
import PdfViewer from '../components/PdfViewer';
import PatientPicker from '../components/PatientPicker';
import TagInput from '../components/TagInput';
import MarkdownContent from '../components/MarkdownContent';
import { useDialog } from '../contexts/ConfirmContext';

// Port aus routes/eingang/pruefen/+page.svelte — Original links, Pruef-Formular rechts.
// Patient steht PROMINENT zuoberst (Verwechslungsschutz).

const TYP_OPTIONS = ['befund', 'arztbrief', 'labor', 'rezept', 'visite', 'bericht', 'stammdaten'];
const fmtDate = (iso: string | null) => { const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/); return m ? `${m[3]}.${m[2]}.${m[1]}` : String(iso ?? ''); };
const patientLabel = (p: Patient) => { const n = `${p.vorname ?? ''} ${p.nachname ?? ''}`.trim() || p.patient_id; return p.geburtsdatum ? `${n} · geb. ${fmtDate(p.geburtsdatum)}` : n; };
const cleanMd = (t: string) => t.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, '').replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, '$2').replace(/\[\[([^\]]+)\]\]/g, '$1').trim();

const Feld = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <label className="block"><span className="text-[11px] font-medium uppercase tracking-wide text-ps-dark-500">{label}</span>{children}</label>
);

export default function Pruefen() {
  const [params] = useSearchParams();
  const ref = params.get('ref') ?? '';
  const navigate = useNavigate();
  const { confirm } = useDialog();

  const [item, setItem] = useState<ReviewItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fallbackText, setFallbackText] = useState<string | null>(null);

  const [patientMode, setPatientMode] = useState<'existing' | 'new'>('existing');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [metaVorname, setMetaVorname] = useState('');
  const [metaNachname, setMetaNachname] = useState('');
  const [metaGeburtsdatum, setMetaGeburtsdatum] = useState('');
  const [typ, setTyp] = useState('');
  const [datum, setDatum] = useState('');
  const [fachgebiet, setFachgebiet] = useState('');
  const [kurzSummary, setKurzSummary] = useState('');
  const [diagnosen, setDiagnosen] = useState<string[]>([]);
  const [medikamente, setMedikamente] = useState<string[]>([]);
  const [behandlung, setBehandlung] = useState<string[]>([]);
  const [committed, setCommitted] = useState<{ undo_token: string; undo_source: 'inbox' | 'library'; patient_id: string } | null>(null);
  const [undone, setUndone] = useState(false);
  const [kiHint, setKiHint] = useState('');
  const [kiBusy, setKiBusy] = useState(false);
  const [kiNote, setKiNote] = useState<string | null>(null);
  const [kiChanged, setKiChanged] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      let it: ReviewItemDetail;
      try { it = await getReviewItem(ref); } catch { setNotFound(true); setLoading(false); return; }
      setItem(it);
      const r = it.recognized ?? {};
      setTyp(String(r.typ ?? r.dokumenttyp ?? '')); setDatum(String(r.datum ?? '')); setFachgebiet(String(r.fachgebiet ?? ''));
      setKurzSummary(String(r.kurz_summary ?? ''));
      setDiagnosen(Array.isArray(r.diagnosen) ? r.diagnosen.map(String) : []);
      setMedikamente(Array.isArray(r.medikamente) ? r.medikamente.map(String) : []);
      setBehandlung(Array.isArray(r.behandlung) ? r.behandlung.map(String) : []);
      const recPid = String(it.patient_id ?? r.patient_id ?? '');
      const meta = (r.patient_meta ?? {}) as Record<string, string>;
      setMetaVorname(meta.vorname ?? ''); setMetaNachname(meta.nachname ?? ''); setMetaGeburtsdatum(meta.geburtsdatum ?? '');
      let sel: Patient | null = null;
      try {
        // #19: gezielt laden — die Liste enthielt nur die ersten 200 Akten.
        if (recPid && recPid !== 'UNKNOWN') {
          const d: any = await getPatient(recPid);
          if (d?.patient_id) sel = { patient_id: d.patient_id, vorname: d.stammdaten?.vorname ?? null, nachname: d.stammdaten?.nachname ?? null, geburtsdatum: d.stammdaten?.geburtsdatum ?? null, station: null, tentative: false, file_count: 0 };
        }
      } catch { /* ohne Liste geht es weiter */ }
      setSelectedPatient(sel);
      if (!sel && (meta.vorname || meta.nachname)) setPatientMode('new');
      if (!it.pdf_url && it.source === 'library' && it.patient_id) {
        try { setFallbackText(await getPatientFile(it.patient_id, it.filename)); } catch { /* kein Text */ }
      }
      setLoading(false);
    })();
  }, [ref]);

  const patientOk = patientMode === 'existing' ? !!selectedPatient : !!(metaVorname.trim() && metaNachname.trim() && metaGeburtsdatum.trim());
  const formOk = patientOk && typ.trim() !== '';

  const commit = async () => {
    if (!item || !formOk || busy) return;
    setBusy(true); setError(null);
    try {
      setCommitted(await reviewCommitForm({
        ref,
        patient_id: patientMode === 'existing' ? (selectedPatient?.patient_id ?? '') : '',
        patient_meta: patientMode === 'new' ? { vorname: metaVorname.trim(), nachname: metaNachname.trim(), geburtsdatum: metaGeburtsdatum.trim() } : null,
        typ: typ.trim(), datum: datum.trim() || null, fachgebiet: fachgebiet.trim() || null,
        diagnosen, medikamente, behandlung, kurz_summary: kurzSummary.trim(),
      }));
    } catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setBusy(false); }
  };
  const discard = async () => {
    if (!item || busy) return;
    if (!await confirm({ title: 'Dokument verwerfen?', message: 'Das Dokument wird aus dem Eingang entfernt und NICHT in eine Akte abgelegt.', okLabel: 'Verwerfen', danger: true })) return;
    setBusy(true); setError(null);
    try { await reviewDiscard(ref); navigate('/eingang'); }
    catch (e: any) { setError(String(e?.message ?? e)); setBusy(false); }
  };
  const undo = async () => {
    if (!committed || busy) return;
    setBusy(true);
    try { await reviewUndo(committed.undo_token, committed.undo_source); setUndone(true); } finally { setBusy(false); }
  };
  const openKiChat = async () => {
    if (busy) return;
    setBusy(true);
    try { navigate(`/chat?chat=${encodeURIComponent(await openReview(ref))}`); }
    catch (e: any) { setError(String(e?.message ?? e)); setBusy(false); }
  };
  const analyze = async () => {
    if (kiBusy || busy) return;
    setKiBusy(true); setError(null); setKiNote(null); setKiChanged([]);
    try {
      const res = await reviewAnalyze({ ref, hint: kiHint, current: {
        typ, datum, fachgebiet, kurz_summary: kurzSummary, diagnosen, medikamente, behandlung,
        patient: patientMode === 'existing' ? (selectedPatient ? patientLabel(selectedPatient) : '(noch keiner gewählt)') : `${metaVorname} ${metaNachname} geb. ${metaGeburtsdatum}`.trim(),
      } });
      const f = res.fields ?? {}; const changed: string[] = [];
      const setStr = (label: string, cur: string, val: unknown, apply: (v: string) => void) => { const v = String(val ?? '').trim(); if (v && v !== cur.trim()) { apply(v); changed.push(label); } };
      setStr('Typ', typ, f.typ, setTyp); setStr('Datum', datum, f.datum, setDatum); setStr('Fachgebiet', fachgebiet, f.fachgebiet, setFachgebiet); setStr('Zusammenfassung', kurzSummary, f.kurz_summary, setKurzSummary);
      const setList = (label: string, cur: string[], val: unknown, apply: (v: string[]) => void) => { if (Array.isArray(val) && val.length && JSON.stringify(val) !== JSON.stringify(cur)) { apply(val.map(String)); changed.push(label); } };
      setList('Diagnosen', diagnosen, f.diagnosen, setDiagnosen); setList('Medikamente', medikamente, f.medikamente, setMedikamente); setList('Behandlung', behandlung, f.behandlung, setBehandlung);
      if (!selectedPatient && (f.patient_vorname || f.patient_nachname)) {
        if (!metaVorname) setMetaVorname(String(f.patient_vorname ?? ''));
        if (!metaNachname) setMetaNachname(String(f.patient_nachname ?? ''));
        if (!metaGeburtsdatum) setMetaGeburtsdatum(String(f.patient_geburtsdatum ?? ''));
      }
      setKiChanged(changed);
      setKiNote(String(f.hinweis ?? '').trim() || 'Analyse abgeschlossen — keine Änderungen nötig.');
    } catch (e: any) { setError(String(e?.message ?? e)); }
    finally { setKiBusy(false); }
  };

  if (loading) return <div className="card p-6"><div className="shimmer h-24 rounded" /></div>;
  if (notFound) return (
    <div className="card p-10 text-center">
      <div className="page-h1 mb-2">Dokument nicht (mehr) in der Queue</div>
      <p className="mb-3 text-sm text-ps-dark-400">Vielleicht wurde es bereits geprüft.</p>
      <Link to="/eingang" className="text-sm font-medium text-primary-500">← Zurück zum Eingang</Link>
    </div>
  );
  if (committed) return (
    <div className="card mx-auto max-w-xl p-8 text-center">
      {undone ? (<>
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-amber-50"><ArrowUturnLeftIcon className="h-5 w-5 text-amber-700" /></div>
        <div className="page-h1 mb-1.5">Rückgängig gemacht</div>
        <p className="mb-5 text-sm text-ps-dark-500">Das Dokument liegt wieder im Eingang.</p>
        <Link to="/eingang" className="btn-primary inline-block">← Zurück zum Eingang</Link>
      </>) : (<>
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-50"><CheckIcon className="h-5 w-5 text-emerald-700" /></div>
        <div className="page-h1 mb-1.5">Abgelegt ✓</div>
        <p className="mb-5 text-sm text-ps-dark-500">Das Dokument wurde in der Akte <Link className="font-medium text-primary-500 hover:underline" to={`/patienten/${encodeURIComponent(committed.patient_id)}`}>{committed.patient_id}</Link> abgelegt.</p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Link to="/eingang" className="btn-primary">Nächstes Dokument prüfen</Link>
          <Link to={`/patienten/${encodeURIComponent(committed.patient_id)}`} className="btn-ghost">Akte öffnen</Link>
          <button onClick={undo} disabled={busy} className="btn-outline">↩ Rückgängig</button>
        </div>
      </>)}
    </div>
  );
  if (!item) return null;

  return (
    <div className="space-y-4">
      {pickerOpen && <PatientPicker onSelect={(p) => { setSelectedPatient(p); setPatientMode('existing'); setPickerOpen(false); }} onCancel={() => setPickerOpen(false)} />}

      <div className="card">
        <Link to="/eingang" className="text-xs text-ps-dark-400 hover:text-ps-dark-700">← Eingang</Link>
        <h1 className="page-h1 mt-0.5">Dokument prüfen</h1>
        <div className="truncate text-xs text-ps-dark-400" title={item.filename}>{item.source === 'inbox' ? 'aus Mail-Import' : 'Zuordnung prüfen'} · {item.filename}</div>
      </div>
      {error && <div className="card bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_420px] [&>*]:min-w-0">
        <div className="card overflow-hidden p-0 lg:sticky lg:top-4">
          {item.pdf_url ? (
            <div className="h-[50vh] lg:h-[78vh]"><PdfViewer src={item.pdf_url} title={item.filename} minHeight="100%" /></div>
          ) : fallbackText ? (<>
            <div className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2.5 text-[12px] text-amber-800">
              <span><strong>Kein PDF-Original vorhanden</strong> — dieses Dokument wurde beim Import aus dem SimED-Datenexport (CSV) erzeugt. Unten der vollständige Inhalt.</span>
            </div>
            <div className="h-[50vh] overflow-auto p-6 lg:h-[72vh]"><MarkdownContent text={cleanMd(fallbackText)} /></div>
          </>) : <div className="flex h-[40vh] items-center justify-center p-8 text-sm text-ps-dark-400">Kein Original-Dokument vorhanden.</div>}
        </div>

        <div className="card space-y-4">
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-semibold text-ps-dark-700">Patient</span>
              <div className="seg">
                <button className={`seg-btn !py-1 text-xs ${patientMode === 'existing' ? 'seg-active' : ''}`} onClick={() => setPatientMode('existing')}>Bestehend</button>
                <button className={`seg-btn !py-1 text-xs ${patientMode === 'new' ? 'seg-active' : ''}`} onClick={() => setPatientMode('new')}>Neu anlegen</button>
              </div>
            </div>
            {patientMode === 'existing' ? (
              selectedPatient ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-gray-200 bg-gray-50 p-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-ps-dark-900">{patientLabel(selectedPatient)}</div>
                    <div className="text-[11px] text-ps-dark-400">{selectedPatient.patient_id}</div>
                  </div>
                  <button onClick={() => setPickerOpen(true)} className="btn-ghost shrink-0 px-2.5 py-1 text-[12px]">Ändern</button>
                </div>
              ) : (
                <button onClick={() => setPickerOpen(true)} className="w-full rounded-md border border-dashed border-rose-500 bg-rose-50 p-2.5 text-sm font-medium text-rose-700 transition-colors hover:bg-rose-100">
                  ⚠ Patient wählen — Pflicht vor dem Ablegen
                </button>
              )
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input className="input-field" placeholder="Vorname *" value={metaVorname} onChange={(e) => setMetaVorname(e.target.value)} />
                  <input className="input-field" placeholder="Nachname *" value={metaNachname} onChange={(e) => setMetaNachname(e.target.value)} />
                </div>
                <input className="input-field" type="date" value={metaGeburtsdatum} onChange={(e) => setMetaGeburtsdatum(e.target.value)} />
                <p className="text-[11px] text-ps-dark-400">Legt beim Ablegen automatisch eine neue Patientenakte an.</p>
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          <div className="grid grid-cols-2 gap-2.5">
            <Feld label="Dokumenttyp *">
              <select className="input-field mt-1" value={typ} onChange={(e) => setTyp(e.target.value)}>
                {typ && !TYP_OPTIONS.includes(typ) && <option value={typ}>{typ}</option>}
                {TYP_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Feld>
            <Feld label="Datum"><input className="input-field mt-1" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} /></Feld>
          </div>
          <Feld label="Fachgebiet"><input className="input-field mt-1" placeholder="z.B. Kardiologie" value={fachgebiet} onChange={(e) => setFachgebiet(e.target.value)} /></Feld>
          <Feld label="Kurz-Zusammenfassung"><textarea className="input-field mt-1" rows={2} placeholder="1–2 Sätze, was das Dokument enthält" value={kurzSummary} onChange={(e) => setKurzSummary(e.target.value)} /></Feld>

          <TagInput label="Diagnosen" items={diagnosen} onChange={setDiagnosen} placeholder="Diagnose hinzufügen …" accent="brand" />
          <TagInput label="Medikamente" items={medikamente} onChange={setMedikamente} placeholder="Medikament hinzufügen …" accent="warn" />
          <TagInput label="Behandlung" items={behandlung} onChange={setBehandlung} placeholder="Behandlung hinzufügen …" />

          <hr className="border-gray-200" />

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="flex items-center gap-1 text-sm font-semibold text-ps-dark-700"><MagnifyingGlassIcon className="h-4 w-4" /> KI-Prüfung</span>
              <button onClick={openKiChat} disabled={busy || kiBusy} className="text-[11px] text-ps-dark-400 transition-colors hover:text-primary-500">Ausführlich im Prüf-Chat →</button>
            </div>
            <textarea className="input-field" rows={2} value={kiHint} onChange={(e) => setKiHint(e.target.value)}
              placeholder='Hinweis an die KI (optional) — z.B. „Das Datum steht oben rechts" oder „Unten fehlt eine Diagnose"' />
            <button onClick={analyze} disabled={kiBusy || busy} className="btn-ghost mt-2 flex w-full items-center justify-center gap-2 border border-gray-200">
              {kiBusy ? <><span className="h-3 w-3 animate-spin rounded-full border-2 border-primary-300 border-t-primary-500" /> Analysiere Original … (kann ~1 Min. dauern)</> : 'Dokument mit KI neu analysieren'}
            </button>
            {kiNote && (
              <div className="mt-2 rounded-md border border-primary-300 bg-primary-50 p-2.5 text-[12.5px] text-ps-dark-900">
                {kiNote}{kiChanged.length > 0 && <div className="mt-1 text-[11px] text-ps-dark-500">Aktualisierte Felder: {kiChanged.join(', ')}</div>}
              </div>
            )}
          </div>

          <hr className="border-gray-200" />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <button onClick={discard} disabled={busy} className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50">Verwerfen</button>
            <button onClick={commit} disabled={!formOk || busy} className="btn-primary flex items-center gap-2" title={formOk ? 'In die Patientenakte ablegen' : 'Patient und Dokumenttyp sind Pflicht'}>
              {busy ? <><span className="h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white" /> Lege ab …</> : '✓ Bestätigen & ablegen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
